import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/ApiClientContext';
import { BehaviourWithHabitsResponse } from '../../api/Api';
import styles from './styles.module.scss';
import { Icon } from '../../assets/icons/Icon';
import Button from '../button/Button';

interface AboutProgramStepProps {
  selectedHabits: string[];
  setSelectedHabits: React.Dispatch<React.SetStateAction<string[]>>;
  currentStep: number;
  onStepChange: (step: number) => void;
  selectedBehaviour?: BehaviourWithHabitsResponse | null;
  onBehaviourSelect?: (behaviour: BehaviourWithHabitsResponse | null) => void;
  programmeId?: string;
  moduleId?: string;
  moduleName?: string;
}

const ICON_NAMES = ['OneIcon', 'TwoIcon', 'ThreeIcon'] as const;

const AboutProgramStep: React.FC<AboutProgramStepProps> = ({
  selectedHabits,
  setSelectedHabits,
  currentStep,
  onStepChange,
  selectedBehaviour: propSelectedBehaviour,
  onBehaviourSelect,
  programmeId,
  moduleId,
  moduleName,
}) => {
  const [selectedBehaviour, setSelectedBehaviour] = useState<BehaviourWithHabitsResponse | null>(propSelectedBehaviour || null);
  const apiClient = useApiClient();

  const { data: programmeBehaviours } = useQuery<BehaviourWithHabitsResponse[]>({
    queryKey: ['programmeBehaviours', programmeId, moduleId],
    enabled: !!programmeId,
    queryFn: async () => {
      const res = await apiClient.api.getProgrammeBehavioursWithHabitsApiV1ProgrammesProgrammeIdBehavioursGet(
        programmeId!,
        moduleId ? { module_id: moduleId } : {},
      );
      return res?.data || [];
    },
  });

  const handleBehaviourSelect = (behaviour: BehaviourWithHabitsResponse) => {
    setSelectedBehaviour(behaviour);
    onBehaviourSelect?.(behaviour);
  };

  const handleCancelSelection = () => {
    setSelectedBehaviour(null);
    onBehaviourSelect?.(null);
  };

  const behaviourIcons = useMemo(() => {
    if (!programmeBehaviours) return {};
    const shuffled = [...ICON_NAMES].sort(() => Math.random() - 0.5);
    const iconMap: Record<string, typeof ICON_NAMES[number]> = {};
    programmeBehaviours.forEach((b, i) => {
      iconMap[b.id] = shuffled[i % shuffled.length];
    });
    return iconMap;
  }, [programmeBehaviours]);

  if (!programmeBehaviours || programmeBehaviours.length === 0) {
    return null;
  }

  return (
    <div className={styles.habitsBlock}>
      <div className={styles.stepper}>
        <div className={styles.stepperItem}>
          <div className={`${styles.stepperCircle} ${styles.stepperCircleActive}`}>1</div>
          <span className={styles.stepperLabel}>Behaviour selection</span>
        </div>
        <div className={styles.stepperLine} />
        <div className={styles.stepperItem}>
          <div className={styles.stepperCircle}>2</div>
          <span className={styles.stepperLabel}>Habit selection</span>
        </div>
      </div>

      <div className={styles.habitsBlockHeader}>
        <h2 className={styles.habitsBlockTitle}>Select behaviour to develop</h2>
        {moduleName && (
          <p className={styles.habitsBlockDescription}>{moduleName}</p>
        )}
      </div>

      <div className={styles.habitsBlockSection}>
        <div className={styles.habitsBlockSectionHeader}>
          <div className={styles.habitsBlockSectionHeaderTitle}>
            Please select 1 behaviour to develop
          </div>
          <div className={styles.habitsBlockSectionHeaderCounter}>
            SELECTED {selectedBehaviour ? 1 : 0}/1
          </div>
        </div>

        <div className={styles.habitsBlockSectionItems}>
          {programmeBehaviours.map((behaviour) => {
            const isSelected = selectedBehaviour?.id === behaviour.id;
            return (
              <div
                className={`${styles.habitsBlockSectionItem} ${isSelected ? styles.selected : ''}`}
                key={behaviour.id}
                onClick={() => isSelected ? handleCancelSelection() : handleBehaviourSelect(behaviour)}
              >
                <div className={styles.habitsBlockSectionItemContent}>
                  <div className={styles.habitsBlockSectionItemContentLeft}>
                    <div className={styles.habitsBlockSectionItemContentIcon}>
                      <Icon iconName={behaviourIcons[behaviour.id] || 'OneIcon'} width="3.5rem" height="3.5rem" />
                    </div>
                    <div className={styles.habitsBlockSectionItemContentBody}>
                      <h3>{behaviour.name}</h3>
                      {behaviour.description && <p>{behaviour.description}</p>}
                    </div>
                  </div>
                  <div className={styles.habitsBlockSectionItemContentAction}>
                    {isSelected ? (
                      <div className={styles.habitsBlockSectionItemContentSelected}>
                        <Icon iconName="GreenCheckIcon2" width="1rem" height="1rem" />
                        <div className={styles.habitsBlockSectionItemContentCancel}>Cancel selection</div>
                      </div>
                    ) : (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBehaviourSelect(behaviour);
                        }}
                        variant="outline"
                        size="small"
                      >
                        Select
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AboutProgramStep;
